import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { Link as RouterLink, useLoaderData, useNavigate } from 'react-router-dom';
import { apiAdmin } from '@/api/admin';
import { ApiError } from '@/api/errors';
import type { OidcClientResponse } from '@/api/types';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { PageHeader } from '@/components/PageHeader';
import { useCopy } from '@/hooks/useCopy';
import { useCurrentOperator } from '@/auth/AuthContext';

export interface OidcClientsListLoaderData {
  clients: OidcClientResponse[] | null;
  disabled: boolean;
}

export async function oidcClientsListLoader(): Promise<OidcClientsListLoaderData> {
  try {
    const clients = await apiAdmin.oidcClients.list();
    return { clients, disabled: false };
  } catch (err) {
    if (err instanceof ApiError && err.code === 'OIDC_PROVIDER_DISABLED') {
      return { clients: null, disabled: true };
    }
    throw err;
  }
}

export function OidcClientsList() {
  const { clients, disabled } = useLoaderData() as OidcClientsListLoaderData;
  const { t } = useCopy();
  const navigate = useNavigate();
  const operator = useCurrentOperator();

  return (
    <Box>
      <Breadcrumbs
        items={[
          { label: t('nav.home', 'Home'), to: '/' },
          { label: t('nav.reference', 'Reference') },
          { label: t('nav.oidcClients', 'OIDC clients') },
        ]}
      />
      <PageHeader
        title={t('oidc.list.title', 'OIDC clients')}
        subtitle={t('oidc.list.subtitle', '')}
        actions={
          operator?.isSuperAdmin ? (
            <Button
              component={RouterLink}
              to="/reference/oidc-clients/new"
              variant="contained"
              startIcon={<AddIcon />}
            >
              {t('oidc.list.create', 'Create client')}
            </Button>
          ) : null
        }
      />
      {disabled ? <Alert severity="warning">{t('oidc.providerDisabled', '')}</Alert> : null}
      {!disabled && clients ? (
        clients.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
            {t('oidc.list.empty', 'No OIDC clients registered.')}
          </Paper>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Client ID</TableCell>
                  <TableCell>Integration</TableCell>
                  <TableCell>{t('oidc.list.accessScope', 'Access scope')}</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {clients.map((c) => (
                  <TableRow
                    key={c.clientDbId}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/reference/oidc-clients/${c.clientDbId}`)}
                  >
                    <TableCell>{c.clientName}</TableCell>
                    <TableCell>{c.clientId}</TableCell>
                    <TableCell>
                      <Chip label={c.integrationType} size="small" />
                    </TableCell>
                    <TableCell>
                      {c.accessScope === 'PRODUCT'
                        ? t('oidc.list.scopeProduct', 'Product ({{count}})', {
                            count: c.allowedProductIds?.length ?? 0,
                          })
                        : t('oidc.list.scopePlatform', 'Platform')}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={c.enabled === false ? 'Disabled' : 'Active'}
                        color={c.enabled === false ? 'default' : 'success'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      ) : null}
    </Box>
  );
}
